import {
  CHAT_MESSAGE_ACTIONS,
  ChatRole,
  ConversationFlow,
  ICharacter,
  IConversationMessage,
  NotificationType,
  useApp,
  useAppSelector,
  useTranslation,
  useUserInfo,
  useUserSettings,
} from "@next-platform/client-common-core";
import {
  CapabilityKey,
  CapabilityUnavailableBehavior,
  SubCapabilityKey,
  useApplicationCapabilities,
  useApplicationCapabilitiesAllowed,
} from "@next-platform/client-common-core/infra/application/capabilities";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import {
  Alert,
  AlertActions,
  AlertVariant,
  AvatarSize,
  ButtonIcon,
  ButtonVariant,
  Checkbox,
  DropDownMenu,
  IconVariants,
  Size,
  Text,
  TextVariants,
} from "@next-platform/client-common-spicy-ui";
import { CapabilityResolution } from "@next-platform/packages-ts-types/application/types/capabilities";
import React, { JSX, useCallback, useState } from "react";
import { useParams } from "react-router-dom";

import { SiteLogo } from "@/components";
import AvatarWithModal from "@/components/common/AvatarWithModal/AvatarWithModal";
import { Col, LinkWrapper, Row } from "@/components/customComponents";
import { CHAT_PAGE_WIDTH, QueueStatus } from "@/constants";
import getUserPlan from "@/features/user/utils/getUserPlan";
import { useAgeVerificationFlags } from "@/hooks";
import useToast from "@/hooks/useToast";
import CapabilityModalConversationImageSettings from "@/pages/Chat/components/CapabilityModalConversationImageSettings";
import ChatBar from "@/pages/Chat/components/ChatBubble/ChatBar";
import { RemoveImageConfirmationModal } from "@/pages/Chat/components/RemoveImageConfirmationModal";
import RulebookDeltaChips from "@/pages/Chat/components/RulebookHud/RulebookDeltaChips";
import getIsChatImageBlurred from "@/pages/Chat/utils/getIsChatImageBlurred";
import { AvatarPreset } from "@/types";
import { cn, getAvatarUrl, getProfilePathForCharacter } from "@/utils";

import { PREFERRED_VOICE_ACTIONS } from "../voices/constant";
import ChatBubbleContent from "./ChatBubbleContent";
import ChatBubbleEditBox from "./ChatBubbleEditBox";
import { ANONYMOUS_DISPLAY_NAME } from "./constants";
import { ChatBarProps, ChatBubbleProps, ChatBubbleVariants } from "./types";
import { getBubbleStyles, getMaxWidthClasses, getNsfwCtaModes } from "./utils";

const logger = appLogger.withTag("ChatBubble");

// Main ChatBubble component
export const ChatBubble = ({
  character,
  message,
  user_name: userName,
  user_avatar_url,
  isTyping,
  onRegenerate,
  isSelectedForRemoval,
  onRemovalSelected,
  setConversation,
  currentConversationId,
  createConversation,
  removalMode,
  loadMessages,
  altMsgs = [],
  chatIndex,
  isClonePartial,
  isSelectedForClone,
  onCloneSelected,
  conversationLength,
  isRemoveMessageLoading,
  handleFlagModalOpen,
  voiceId,
  infoMessage,
  handlePreferredVoiceActions,
  showCheckbox,
  requestErrorHandler,
  isMessageEditMode, // track if edit mode enabled in any of the message
  setIsMessageEditMode,
  children,
  isChatWithGroup,
  handleEditPendingMessage,
  onUserUpdateMessage,
  isLoading,
  loadingType,
  handleDeleteMessageImage,
  isImageDeleteFromMessageLoading,
  onOpenImageDetails,
}: ChatBubbleProps): JSX.Element => {
  // A message written since the Gallery landed names its file; an older one keeps the plain zoom.
  const mediaFileId = message?.media_file_id;
  const handleOpenImageDetails = useCallback(() => {
    if (mediaFileId && onOpenImageDetails) {
      onOpenImageDetails(mediaFileId);
    }
  }, [mediaFileId, onOpenImageDetails]);
  const { t } = useTranslation();
  const { displayToast } = useToast();
  const { isNsfwEnabled } = useApp();

  const [isImageSettingsModalOpen, setIsImageSettingsModalOpen] = useState(false);
  const [isRemoveImageConfirmationModalOpen, setIsRemoveImageConfirmationModalOpen] = useState(false);
  const isSaveMessageLoading = isLoading && loadingType === ConversationFlow.UPDATE_MESSAGE;
  const [messageEditMode, setMessageEditMode] = useState<"text" | "image" | null>(null);
  const isImageEditMode = messageEditMode === "image";
  const [editMessageContent, setEditMessageContent] = useState<string | undefined>(message?.content);
  const { blurNSFW } = useUserSettings();
  const { isAuthenticated } = useUserInfo();
  const nsfwChat = useApplicationCapabilities(SubCapabilityKey.NsfwChat);
  const nsfwImage = useApplicationCapabilities(SubCapabilityKey.NsfwImage);
  const nsfw = useApplicationCapabilities(CapabilityKey.Nsfw);
  const { isAgeVerificationRequired } = useAgeVerificationFlags();
  const { isUserPremium } = getUserPlan();
  const isTtsAllowed = useApplicationCapabilitiesAllowed(CapabilityKey.Tts);
  const { status: waitingQueueStatus } = useAppSelector(({ waitingQueue }) => waitingQueue) || {};
  const { characterId } = useParams();
  //if conversation length is 1, it means it is a new conversation and user can edit the greeting message
  const isEditableChatExperienceEnabled = waitingQueueStatus !== QueueStatus.WAITING || conversationLength === 1;
  // The same rule the chat page applies to every file it hands the details modal, so the modal agrees with the bubble.
  const isAvatarImageBlurred = getIsChatImageBlurred({ character, message, blurNSFW, isAuthenticated });
  const regeneratable =
    altMsgs?.length > 0 || (chatIndex > 0 && conversationLength - 1 === chatIndex && message?.role === ChatRole.BOT && !altMsgs?.length);
  const allowMessageActions = regeneratable && !messageEditMode && !isTyping && isEditableChatExperienceEnabled && !isClonePartial && !removalMode;

  const isThisAltMessage = altMsgs?.length > 0;
  const { text: textNsfwCtaMode, image: imageNsfwCtaMode } = getNsfwCtaModes(message, {
    isNsfwEnabled,
    isNsfwChatAllowed: !!nsfwChat?.allowed,
    isNsfwChatPaywalled: nsfwChat?.unavailableBehavior === CapabilityUnavailableBehavior.SHOW_PAYWALL,
    isNsfwImageAllowed: !!nsfwImage?.allowed,
    isAgeVerificationRequired,
  });
  const isTextNsfwGatingEnabled = textNsfwCtaMode !== undefined;
  const isImageNsfwGatingEnabled = imageNsfwCtaMode !== undefined;

  const isNsfwGatingEnabled = isTextNsfwGatingEnabled || isImageNsfwGatingEnabled;

  const isBotMessage = message?.role === ChatRole.BOT;
  const isUserMessage = message?.role === ChatRole.USER;
  const messageImageUrl = message?.image_url;

  // User-uploaded image message: USER role with image_url but no image_prompt.
  // Unlike bot-generated images (which have image_prompt), these can have their text content edited.
  const isUserUploadedImageMessage = isUserMessage && !!messageImageUrl && !message?.image_prompt;

  // Allow text editing: no image OR user-uploaded image message.
  const canEditMessageWithImage = !messageImageUrl || isUserUploadedImageMessage;

  const allowEditAndCopy = !removalMode && !isClonePartial && !isTyping && !messageEditMode && canEditMessageWithImage;
  const isDropdownActionsDisabled = isMessageEditMode || !!isTyping;
  const showActionsDropdown = allowMessageActions || allowEditAndCopy;
  const allowRatingVoiceQuality = message?.id && isBotMessage && voiceId && isUserPremium && isTtsAllowed && !message?.image_url && !isChatWithGroup;
  const showInfoMessage = infoMessage?.messageId === message?.id;
  const messageAvatarUrl = message?.role === ChatRole.USER ? user_avatar_url : character?.avatar_url;
  const messageName = message?.role === ChatRole.USER ? userName || ANONYMOUS_DISPLAY_NAME : character?.name;
  const conversationUserName = userName || ANONYMOUS_DISPLAY_NAME;
  const imageSrc = getAvatarUrl(messageAvatarUrl ?? "", { preset: AvatarPreset.AVATAR_64X64 });
  const user = {
    src: imageSrc,
    name: messageName,
    blurred: isAvatarImageBlurred,
  };
  const nameFirstLetter = user?.name?.charAt(0)?.toUpperCase();
  const shouldShowImageRemoveAction = isUserUploadedImageMessage && !!message?.content?.trim()?.length;

  const onRemoveImageConfirmation = async () => {
    await handleDeleteMessageImage(message.id);
    setIsRemoveImageConfirmationModalOpen(false);
  };

  const saveMessage = async () => {
    let prevEntry: IConversationMessage | undefined;
    let prevValueIndex: number;
    // Editing the greeting invalidates its pre-generated audio, so drop the URL locally to match the
    // server clearing greeting_audio_reference_key on update.
    setConversation((prevConversation: IConversationMessage[]) => {
      return prevConversation.map((entry: IConversationMessage, i: number) => {
        if (entry?.id === message?.id) {
          prevEntry = entry;
          prevValueIndex = i;
          return { ...entry, content: editMessageContent ?? "", greeting_audio_url: undefined };
        }
        return entry;
      });
    });
    if (message?.id) {
      const { error } = await onUserUpdateMessage({ id: message?.id, content: editMessageContent ?? "" });
      if (error && prevEntry) {
        // If message update failed, restore the whole previous entry (content and greeting audio).
        const restoredEntry = prevEntry;
        setConversation((prevConversation: IConversationMessage[]) =>
          prevConversation.map((entry: IConversationMessage, i: number) => (i === prevValueIndex ? restoredEntry : entry)),
        );
      }
    } else if (!currentConversationId && chatIndex === 0) {
      // we need to create the conversation and save the greeting
      if (createConversation) {
        await createConversation([{ role: ChatRole.BOT, content: editMessageContent || "" }]);
      }
    }
  };

  const editChatMessage = async () => {
    setMessageEditMode("text");
    setEditMessageContent(message?.content);
    setIsMessageEditMode(true);
    // message.id is needed to update message content. Whenever undefined, we need to call loadMessage() to fetch   it from backend.
    if (currentConversationId && !message?.id) {
      if (loadMessages) {
        loadMessages();
      }
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message?.content ?? "");
      displayToast({ message: t("chat:page.chatBubble.copiedToClipboard.toast.success"), type: NotificationType.success });
    } catch (error) {
      logger.error("Error in handleCopy", error);
    }
  };

  const handleChatMessageActions = (action: string) => {
    document.body.style.overflow = "";
    handlePreferredVoiceActions({ action: PREFERRED_VOICE_ACTIONS.Stop });
    switch (action) {
      case CHAT_MESSAGE_ACTIONS.COPY: {
        return handleCopy();
      }
      case CHAT_MESSAGE_ACTIONS.EDIT: {
        return editChatMessage();
      }
      case CHAT_MESSAGE_ACTIONS.RATE_VOICE: {
        return handlePreferredVoiceActions({
          action: PREFERRED_VOICE_ACTIONS.Rate_Modal,
          messageId: message?.id,
        });
      }
      case CHAT_MESSAGE_ACTIONS.REPORT: {
        return handleFlagModalOpen(message?.id, isThisAltMessage);
      }
      case CHAT_MESSAGE_ACTIONS.REMOVE_IMAGE: {
        return setIsRemoveImageConfirmationModalOpen(true);
      }
    }
  };

  const messageActions = [
    {
      label: t("chat:page.chatBubble.action.copy"),
      icon: IconVariants.COPY,
      onPress: () => handleChatMessageActions(CHAT_MESSAGE_ACTIONS.COPY),
      isHidden: !allowEditAndCopy,
    },
    {
      label: t("chat:page.chatBubble.action.edit"),
      icon: IconVariants.EDIT,
      onPress: () => handleChatMessageActions(CHAT_MESSAGE_ACTIONS.EDIT),
      isHidden: !allowEditAndCopy,
    },
    {
      label: t("chat:page.chatBubble.action.removeImage"),
      icon: IconVariants.TRASH,
      onPress: () => handleChatMessageActions(CHAT_MESSAGE_ACTIONS.REMOVE_IMAGE),
      isHidden: !shouldShowImageRemoveAction,
    },
    {
      label: t("chat:page.chatBubble.action.rateVoiceQuality"),
      icon: IconVariants.AUDIO,
      onPress: () => handleChatMessageActions(CHAT_MESSAGE_ACTIONS.RATE_VOICE),
      isHidden: !allowRatingVoiceQuality,
    },
    {
      label: t("chat:page.chatBubble.action.report"),
      icon: IconVariants.FLAG,
      onPress: () => handleChatMessageActions(CHAT_MESSAGE_ACTIONS.REPORT),
      isHidden: !allowMessageActions,
    },
  ];

  const getChatBubbleVariant = (wrapper?: boolean) => {
    if (wrapper) {
      if (removalMode && isSelectedForRemoval) {
        return ChatBubbleVariants.DELETE;
      }
      if (isClonePartial && isSelectedForClone) {
        return ChatBubbleVariants.CLONE;
      }
      return;
    }

    return message?.role === ChatRole.USER ? ChatBubbleVariants.USER : ChatBubbleVariants.BOT;
  };

  const handleCheckMessage = () => {
    if (!showCheckbox) return;
    if (isClonePartial) {
      if (isSelectedForClone) {
        onCloneSelected(-1);
      } else {
        onCloneSelected(chatIndex);
      }
    } else {
      if (isSelectedForRemoval) {
        onRemovalSelected(null);
      } else {
        if (!isThisAltMessage) {
          onRemovalSelected(chatIndex);
        } else {
          onRemovalSelected(conversationLength);
        }
      }
    }
  };

  const handleOnEditSave = () => {
    if (message.id === "pending-temp") handleEditPendingMessage?.(editMessageContent ?? message.content);
    else if (isImageEditMode) {
      onRegenerate({ regenerate: true, image_prompt: editMessageContent });
    } else {
      saveMessage();
    }
    setMessageEditMode(null);
    setIsMessageEditMode(false);
  };

  const handleCancelEdit = () => {
    setMessageEditMode(null);
    setIsMessageEditMode(false);
    setEditMessageContent(message?.content);
  };

  const handleProfileNavigation = () => {
    return getProfilePathForCharacter(character as ICharacter);
  };

  const handleResubmitRequest = (action: AlertActions) => {
    if (action === AlertActions.CTA && requestErrorHandler) {
      requestErrorHandler?.actionHandler();
    }
  };

  const isAvatarBubble = !!message?.image_url;
  const isSelected = isSelectedForRemoval || isSelectedForClone;
  const isBotGeneratedImageMessage = isBotMessage && isAvatarBubble;
  const shouldReplaceBubbleWithErrorAlert = requestErrorHandler?.isError && !isNsfwGatingEnabled && !isBotGeneratedImageMessage;
  // A gated reply normally offers a way forward (subscribe, verify age). When the application disables
  // NSFW in its own configuration there is no such path, so explain it instead of showing a placeholder
  // the viewer can never unlock.
  const shouldReplaceBubbleWithNsfwAlert = isTextNsfwGatingEnabled && nsfw?.resolution === CapabilityResolution.DISABLED_BY_CONFIG;

  if (shouldReplaceBubbleWithNsfwAlert) {
    return (
      <Row className="w-full px-md" style={{ maxWidth: CHAT_PAGE_WIDTH }}>
        <Alert
          visible
          variant={AlertVariant.ERROR}
          title={t("chat:chatBubble.nsfwBlocked", "NSFW roleplay isn’t supported. Please try again with a different message.")}
          isDismissable={false}
        />
      </Row>
    );
  }

  if (shouldReplaceBubbleWithErrorAlert) {
    return (
      <Row className="w-full px-md" style={{ maxWidth: CHAT_PAGE_WIDTH }}>
        <Alert
          visible={requestErrorHandler.isError}
          title={requestErrorHandler.errorTitle || requestErrorHandler.errorMessage}
          description={requestErrorHandler.errorTitle ? requestErrorHandler.errorMessage : undefined}
          actionHandler={handleResubmitRequest}
          actionTitle={requestErrorHandler?.actionTitle}
          variant={requestErrorHandler.variant}
          isDismissable={false}
        />
      </Row>
    );
  }

  return (
    <>
      <div
        className={cn("w-full flex justify-end mb-lg bg-transparent items-center", isUserMessage ? "justify-between" : "justify-start")}
        style={{ maxWidth: CHAT_PAGE_WIDTH }}
        onClick={handleCheckMessage}
      >
        <div>
          {
            <>
              {showCheckbox ? (
                <div className={"ml-md"}>
                  <Checkbox
                    size={Size.MEDIUM}
                    className="rounded-sm border-gray-6 dark:border-gray-9"
                    checked={isSelectedForRemoval || isSelectedForClone}
                    disabled={isRemoveMessageLoading}
                  />
                </div>
              ) : null}
            </>
          }
        </div>
        <Col
          className={cn(
            "gap-md  w-full px-[13px] py-md",
            isAvatarBubble || isImageNsfwGatingEnabled ? "!max-w-[404px]" : getMaxWidthClasses(getChatBubbleVariant()),
            getBubbleStyles(isUserMessage),
            (removalMode || isClonePartial) && "mx-md",
            messageEditMode && "!bg-transparent !w-full",
            isSelected && "bg-sky-9 dark:bg-sky-2 text-white",
          )}
          style={{ maxWidth: CHAT_PAGE_WIDTH }}
        >
          <div className={cn("flex justify-between items-center gap-md", isUserMessage && "flex-row-reverse")}>
            <div className={cn("flex items-center gap-md ", isUserMessage && "flex-row-reverse")}>
              <AvatarWithModal
                size={AvatarSize.LARGE}
                id={characterId}
                src={messageAvatarUrl || ""}
                name={nameFirstLetter}
                blurred={user?.blurred}
                role={message?.role}
                classNames={{
                  name: "!text-heading-4",
                  base: cn(!messageAvatarUrl && "bg-purple-9"),
                }}
                children={
                  !isAuthenticated && isUserMessage ? (
                    <div className="bg-black p-sm rounded-md size-11">
                      <SiteLogo type="icon" className="size-full" theme="dark" />
                    </div>
                  ) : null
                }
                isClickable={!showCheckbox}
              />
              {user?.name ? (
                <div className="flex flex-col gap-1">
                  <LinkWrapper to={isBotMessage && !showCheckbox ? handleProfileNavigation() : undefined}>
                    <Text variant={TextVariants.LABEL_LARGE_MEDIUM} className={cn(isUserMessage ? "text-white" : "dark:text-gray-12 text-gray-12")}>
                      {user.name}
                    </Text>
                  </LinkWrapper>
                </div>
              ) : null}
            </div>
            <>
              {showActionsDropdown && !showCheckbox && !!messageActions?.length && !isNsfwGatingEnabled ? (
                <DropDownMenu listItems={messageActions} placement="bottom-end" className={cn(isUserMessage && "left-0")}>
                  <ButtonIcon
                    variant={ButtonVariant.TRANSPARENT}
                    icon={IconVariants.MORE_VERTICAL}
                    className={cn(isUserMessage ? "text-white" : "text-gray-10", "w-[20px]")}
                    disabled={isDropdownActionsDisabled}
                    aria-label="message-dropdown"
                  />
                </DropDownMenu>
              ) : null}
            </>
          </div>
          <div className="flex flex-col gap-md">
            <>
              {!messageEditMode && (
                <div className={cn("flex flex-col w-full [overflow-wrap:anywhere]")}>
                  <ChatBubbleContent
                    message={message}
                    isImageNsfwGatingEnabled={isImageNsfwGatingEnabled}
                    isTextNsfwGatingEnabled={isTextNsfwGatingEnabled}
                    textNsfwCtaMode={textNsfwCtaMode}
                    imageNsfwCtaMode={imageNsfwCtaMode}
                    userName={conversationUserName}
                    characterName={character?.name}
                    isAvatarImageBlurred={user?.blurred ?? false}
                    isSelected={isSelected}
                    showCheckbox={showCheckbox}
                    onOpenImageDetails={mediaFileId && onOpenImageDetails ? handleOpenImageDetails : undefined}
                  />
                  {isBotMessage ? (
                    <RulebookDeltaChips message={message} characterId={characterId} isChatWithGroup={Boolean(isChatWithGroup)} />
                  ) : null}
                </div>
              )}

              {messageEditMode ? (
                <ChatBubbleEditBox
                  value={editMessageContent}
                  onChange={setEditMessageContent}
                  onCancel={handleCancelEdit}
                  onSettings={() => setIsImageSettingsModalOpen(true)}
                  onSave={handleOnEditSave}
                  isSaving={isSaveMessageLoading}
                  isImageEditMode={isImageEditMode}
                />
              ) : null}
            </>
          </div>
          {React.Children.map(children, (child: React.ReactNode) => {
            if (React.isValidElement(child) && child.type === ChatBar) {
              return React.cloneElement(
                child as React.ReactElement<ChatBarProps>,
                {
                  messageEditMode,
                  setMessageEditMode,
                  setEditMessageContent,
                  chatIndex,
                  message,
                } as Partial<ChatBarProps>,
              );
            }
            return child;
          })}
          {showInfoMessage ? (
            <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="text-gray-11">
              {infoMessage?.message}
            </Text>
          ) : null}
        </Col>
      </div>

      {requestErrorHandler?.isError && isBotGeneratedImageMessage && !isNsfwGatingEnabled ? (
        <Row className="w-full px-md" style={{ maxWidth: CHAT_PAGE_WIDTH }}>
          <Alert
            visible={requestErrorHandler.isError}
            title={requestErrorHandler.errorTitle || requestErrorHandler.errorMessage}
            description={requestErrorHandler.errorTitle ? requestErrorHandler.errorMessage : undefined}
            actionHandler={handleResubmitRequest}
            actionTitle={requestErrorHandler?.actionTitle}
            variant={requestErrorHandler.variant}
            isDismissable={false}
          />
        </Row>
      ) : null}

      <CapabilityModalConversationImageSettings isOpen={isImageSettingsModalOpen} setIsOpen={setIsImageSettingsModalOpen} />
      {isRemoveImageConfirmationModalOpen ? (
        <RemoveImageConfirmationModal
          isOpen={isRemoveImageConfirmationModalOpen}
          isLoading={isImageDeleteFromMessageLoading}
          onClose={setIsRemoveImageConfirmationModalOpen}
          onConfirm={onRemoveImageConfirmation}
        />
      ) : null}
    </>
  );
};

export default ChatBubble;
